import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import {
  fetchAlertsDebug,
  triggerAdminTestAlert,
  triggerAdminNotificationRun,
} from "@/lib/sacred-alerts/sacred-alerts-api";
import { Loader2, ArrowLeft, RefreshCw, Send } from "lucide-react";
import { getEditorAuth } from "@/lib/sacred-library/sacred-library-api";

export function AlertsTestPage() {
  const { isAdmin, editorUser } = useSacredAdmin();
  const [email, setEmail] = useState("alerts-test@vedapath.in");
  const [debug, setDebug] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const userRow = debug?.users?.[0];

  const load = async () => {
    setLoading(true);
    setMessage("");
    try {
      const data = await fetchAlertsDebug(email.trim() || undefined);
      setDebug(data);
    } catch (e) {
      setDebug(null);
      setMessage(e?.response?.data?.detail || "Load failed — admin login required");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin && getEditorAuth().token) load();
  }, [isAdmin]);

  const sendTest = async () => {
    try {
      const r = await triggerAdminTestAlert(email.trim());
      setMessage(`Test alert sent to ${r.email} (${r.user_id})`);
      load();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Test alert failed");
    }
  };

  const runCron = async () => {
    try {
      const r = await triggerAdminNotificationRun();
      setMessage(`Cron: ${r.sent} sent, ${r.skipped} skipped`);
      load();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Cron run failed");
    }
  };

  if (!getEditorAuth().token) {
    return (
      <div className="min-h-screen py-24 px-4">
        <Link to="/admin/sacred" className="text-primary underline">Sign in</Link> as admin first.
      </div>
    );
  }

  if (!isAdmin) return <Navigate to="/admin/sacred" replace />;

  return (
    <div className="min-h-screen py-24 px-4" data-testid="alerts-test-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/sacred">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Link>
          </Button>
          <h1 className="font-heading text-2xl">Alerts Test</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Console: <code className="text-xs bg-muted px-1 rounded">testAlerts(&quot;{email}&quot;)</code>
          {editorUser && <> · {editorUser.email}</>}
        </p>

        <Card>
          <CardContent className="pt-6 flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label>User email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
            </div>
            <Button type="button" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Reload
            </Button>
            <Button type="button" variant="secondary" onClick={sendTest}>
              <Send className="w-4 h-4 mr-1" /> Test alert
            </Button>
            <Button type="button" variant="outline" onClick={runCron}>
              Run cron
            </Button>
          </CardContent>
        </Card>

        {message && <p className="text-sm text-muted-foreground">{message}</p>}

        {debug?.summary && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Debug summary ({debug.date})</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <p>Users: {debug.user_count} · Schedules: {debug.summary.total_schedules}</p>
              <p>
                Today — tithi: {debug.summary.today_tithi_birthday_count} · festival/vrata:{" "}
                {debug.summary.today_festival_vrata_count} · sādhana: {debug.summary.today_sadhana_count}
              </p>
            </CardContent>
          </Card>
        )}

        {userRow && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Computed tithi & next alert</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <p>
                  Birth tithi:{" "}
                  <strong>{userRow.birth_tithi?.full_name || userRow.birth_tithi?.name || "—"}</strong>
                </p>
                <p>
                  Next tithi birthday: <strong>{userRow.next_tithi_birthday || "—"}</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  {(userRow.today_alerts || []).map((a, i) => (
                    <Badge key={i} variant="outline">
                      {a.kind}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Raw profile (debug row)</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs overflow-auto max-h-72 p-2 bg-muted rounded">
                    {JSON.stringify(userRow, null, 2)}
                  </pre>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">All scheduled alerts</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs overflow-auto max-h-72 p-2 bg-muted rounded">
                    {JSON.stringify(debug.all_scheduled_alerts, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {!userRow && debug && !loading && (
          <p className="text-sm text-muted-foreground">No user found for that email in the alert index.</p>
        )}
      </div>
    </div>
  );
}
